import { Fragment, useEffect, useRef } from 'react'
import Popper from 'popper.js'

import { useTooltipContainerContext, useTooltipPositionContext, useTooltipDisplayContext } from './tooltip.context'
import { useTooltipStyles } from './tooltip.style'

const OFFSET = 10 // move tooltip so that it's on the bottom-right corner of the cursor

export const TOOLTIP_TYPE = {
  DEFAULT: 'default',
  LIST: 'list',
  MINI: 'mini'
}

export const Tooltip = function Tooltip ({ type = TOOLTIP_TYPE.DEFAULT, defaults, tooltipCoordinates }) {
  const tooltipContainerRef = useTooltipContainerContext()
  const tooltipPositionRef = useTooltipPositionContext()
  const popperRef = useRef()
  const tooltipRootRef = useRef()
  const tooltipRootNodeRef = useRef()
  const tooltipRef = useRef()
  const tooltipCoordinatesRef = useRef()
  const tooltipDataRef = useRef()
  const [tooltipDisplayData] = useTooltipDisplayContext()
  const classes = useTooltipStyles()

  const displayData = Object.assign({}, defaults, tooltipDisplayData)

  if (displayData) {
    tooltipDataRef.current = displayData
  } else if (tooltipDataRef.current) {
    tooltipDataRef.current = undefined
  }

  if ((displayData.secondaryLabel !== undefined) && (displayData.secondaryValue !== undefined)) {
    displayData.secondaryLabel = Array.isArray(displayData.secondaryLabel) ? displayData.secondaryLabel : [displayData.secondaryLabel]
    displayData.secondaryValue = Array.isArray(displayData.secondaryValue) ? displayData.secondaryValue : [displayData.secondaryValue]
  }

  const setTooltipPosition = (x, y) => {
    if (!tooltipRootRef.current) {
      tooltipRootRef.current = tooltipContainerRef.current
    }
    if (!tooltipPositionRef.current) {
      tooltipPositionRef.current = tooltipRootRef.current.querySelector('.tooltip-position')
    }

    const position = { x, y }

    tooltipPositionRef.current.style.top = `${position.y}px`
    tooltipPositionRef.current.style.left = `${position.x}px`
    popperRef.current.scheduleUpdate()
    tooltipCoordinatesRef.current = [position.x, position.y]
  }

  useEffect(() => {
    popperRef.current = new Popper(tooltipPositionRef.current, tooltipRef.current, {
      placement: 'bottom-start',
      modifiers: {
        offset: {
          offset: `${OFFSET},${OFFSET}`
        },
        flip: {
          boundariesElement: tooltipRef.current.parentNode
        }
      }
    })

    function handleMouseEvent (event) {
      tooltipRootNodeRef.current = event.currentTarget
      tooltipRootRef.current = tooltipRootNodeRef.current.getBoundingClientRect()

      // Get position relative to the tooltip root node
      setTooltipPosition(event.x - tooltipRootRef.current.x, event.y - tooltipRootRef.current.y)
    }

    tooltipContainerRef.current.addEventListener('mousemove', handleMouseEvent)
    tooltipContainerRef.current.addEventListener('mouseover', handleMouseEvent)

    return () => {
      if (tooltipContainerRef.current) {
        tooltipContainerRef.current.removeEventListener('mousemove')
        tooltipContainerRef.current.removeEventListener('mouseover')
      }
      popperRef.current.destroy()
    }
  }, [])

  useEffect(() => {
    if (tooltipCoordinates && tooltipCoordinates.length === 2) {
      const [x, y] = tooltipCoordinates
      setTooltipPosition(x, y)
    }
  }, [tooltipCoordinates])

  if (type === TOOLTIP_TYPE.DEFAULT) {
    return (
      <div className={classes.box} ref={tooltipRef} style={{ display: tooltipDisplayData ? 'block' : 'none' }}>
        {tooltipDisplayData && displayData.secondaryLabel && displayData.secondaryValue &&
          displayData.secondaryValue.map((value, index) => (
            <div key={index} className={classes.secondarySection}>
              {displayData.secondaryLabel[index] ? (
                <>
                  <div>{displayData.secondaryLabel[index]}:</div>
                  <div className={classes.secondaryValue}>{value}</div>
                </>
              ) : (
                <div style={{ marginLeft: 0 }} className={classes.secondaryValue}>{value}</div>
              )}
            </div>
          ))
        }
        {displayData.referenceData && (
          <div className={classes.referenceSection}>
            {displayData.referenceData.map(({ value, label }) => (
              <div key={label} className={classes.referenceItem}>
                <div className={classes.referenceLabel}>{label}:</div>
                <div className={classes.referenceValue}>{value}</div>
              </div>
            ))}
          </div>
        )}
        {tooltipDisplayData && displayData.primaryLabel && displayData.primaryValue &&
          <div className={classes.primarySection}>
            <div className={classes.primaryLabel}>{displayData.primaryLabel}:</div>
            <div className={classes.values}>
              <div className={classes.primaryValue}>{displayData.primaryValue}</div>
              { (displayData.tertiaryValue !== undefined) &&
                <div className={classes.tertiaryValue}>({displayData.tertiaryValue})</div>
              }
            </div>
          </div>
        }
      </div>
    )
  }

  if (type === TOOLTIP_TYPE.LIST) {
    return (
      <div className={classes.listBox} ref={tooltipRef} style={{ display: tooltipDisplayData ? 'block' : 'none' }}>
        {displayData && displayData.list && displayData.list.map(({ id, label, color, primaryValue, secondaryValue, bands = [] }) => (
          <Fragment key={id}>
            <div className={classes.seriesRow}>
              <div className={classes.colorBall} style={{ backgroundColor: color }} />
              <div className={classes.seriesLabel}>{label}:</div>
              <div className={classes.seriesValue}>{primaryValue}</div>
              { secondaryValue && <div>({secondaryValue})</div> }
              { bands.length === 1 && <div>({bands[0].value})</div> }
            </div>
            {bands.length > 1 && bands.map(({ label, color, value }) => (
              <div key={label} className={classes.seriesRowBand}>
                <div className={classes.colorBall} style={{ backgroundColor: color }} />
                <div className={classes.seriesLabel}>{label}:</div>
                <div>{value}</div>
              </div>
            ))}
          </Fragment>
        ))
        }
      </div>
    )
  }

  if (type === TOOLTIP_TYPE.MINI) {
    return (
      <div className={classes.miniBox} ref={tooltipRef} style={{ display: tooltipDisplayData ? 'block' : 'none' }}>
        {tooltipDisplayData && displayData && displayData.primaryValue && displayData.primaryValue}
      </div>
    )
  }

  return null
}
